<script>
import Merchandising from "@/services/Merchandising";
import Swal from "sweetalert2";

export default {
  data() {
    return {
      showModal: false,
      submitting: false,
      categories: {},
      form: {
        category_name: "",
        category_slug: "",
        parent_category: 0,
        visibility: true,
      },
    };
  },

  computed: {
    // Flatten the nested categories tree into a single list of parent options.
    // getCategories() returns objects keyed by category code (not arrays), so
    // iterate with Object.values. subcategories/subsubcategories may be {} or [].
    parentOptions() {
      const options = [{ value: 0, text: "— None (top-level) —" }];
      Object.values(this.categories || {}).forEach((category) => {
        options.push({ value: category.ID_Grupa, text: category.name });
        Object.values(category.subcategories || {}).forEach((sub) => {
          options.push({ value: sub.ID_PodGrupa, text: "— " + sub.name });
        });
      });
      return options;
    },
  },

  created() {
    // Load up front so the parent dropdown is populated before the modal opens.
    this.loadCategories();
  },

  methods: {
    onShown() {
      this.resetForm();
      // Refresh so newly-added categories appear as parent options.
      this.loadCategories();
    },

    async loadCategories() {
      try {
        const response = await Merchandising.getCategories();
        this.categories = response.data.data || {};
      } catch (error) {
        this.categories = {};
      }
    },

    resetForm() {
      this.form = {
        category_name: "",
        category_slug: "",
        parent_category: 0,
        visibility: true,
      };
    },

    async submit() {
      if (!this.form.category_name.trim()) {
        Swal.fire("Error", "Category name is required", "error");
        return;
      }

      this.submitting = true;

      const payload = {
        category_name: this.form.category_name.trim(),
        // Selected parent category id (ID_Grupa / ID_PodGrupa); 0 for top-level.
        parent_category: this.form.parent_category || 0,
        visibility: this.form.visibility ? 1 : 0,
      };

      // Slug is optional — when left empty the backend derives it from the name.
      if (this.form.category_slug.trim()) {
        payload.category_slug = this.form.category_slug.trim();
      }

      try {
        await Merchandising.createCategory(payload);
        Swal.fire("Success", "Category successfully added!", "success");
        this.$emit("onRefresh");
        this.closeModal();
      } catch (error) {
        let msg = error.response?.data?.error || "Failed to create category. Please try again.";
        if (Array.isArray(msg)) {
          msg = msg.join(", ");
        }
        Swal.fire("Error", msg, "error");
      } finally {
        this.submitting = false;
      }
    },

    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<template>
  <b-modal
    v-model="showModal"
    @shown="onShown"
    id="add_category"
    title="Add Category"
    title-class="font-18"
    centered
  >
    <div class="row">
      <div class="col-12 mb-3">
        <label class="form-label" for="category_name">
          Name <span class="text-danger">*</span>
        </label>
        <input
          v-model="form.category_name"
          class="form-control"
          id="category_name"
          placeholder="Category name"
        />
      </div>

      <div class="col-12 mb-3">
        <label class="form-label" for="category_slug">Slug</label>
        <input
          v-model="form.category_slug"
          class="form-control"
          id="category_slug"
          placeholder="Defaults to a slug derived from the name"
        />
      </div>

      <div class="col-12 mb-3">
        <label class="form-label" for="parent_category">Parent Category</label>
        <b-form-select
          id="parent_category"
          v-model="form.parent_category"
          :options="parentOptions"
        ></b-form-select>
      </div>

      <div class="col-12 mb-3">
        <div class="form-check form-switch form-switch-lg">
          <input
            v-model="form.visibility"
            type="checkbox"
            class="form-check-input"
            id="category_visibility"
          />
          <label class="form-check-label" for="category_visibility">Visible</label>
        </div>
      </div>
    </div>

    <template v-slot:modal-footer>
      <b-button variant="light" @click="closeModal">Close</b-button>
      <b-button variant="primary" @click="submit" :disabled="submitting">
        <b-spinner v-show="submitting" small></b-spinner>
        Save
      </b-button>
    </template>
  </b-modal>
</template>
